import { withAITracking } from '@microsoft/applicationinsights-react-js';
import Edit from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { unitStructureQuery } from 'api/queries';
import { unitInfoQuery, unitKeys } from 'api/queries/UnitQueries';
import { PdfIcon } from 'assets/images/documents';
import { AreaName } from 'domain/constants';
import { SectionButton } from 'library/components/controls/buttons/SectionButton';
import { SectionTitleButtons } from 'library/components/controls/layout/SectionTitleButtons';
import { useDialogState } from 'library/hooks/useDialogState';
import { OperationType } from 'library/models/enums/OperationType';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';
import { reactPlugin } from 'main/azureInsights/config';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';
import { isUnitMarine } from 'modules/Shared/State/unit';
import {
	resetUnitCreationState,
	selectUnitCreationFiles,
} from 'modules/UnitCreation/State';
import {
	attachmentFolderCreated,
	fileDeleted,
	fileUploaded,
} from 'modules/UnitCreation/State/FilesSlice';
import { AdditionalFileUploadButton } from 'modules/UnitOverview/Components/AdditionalFileUploadButton';
import { AdditionalFilesSection } from 'modules/UnitOverview/Components/AdditionalFilesSection';
import { ChangedByInfoDisplay } from 'modules/UnitOverview/Components/ChangedByInfoDisplay';
import { IAdditionalFileUploadStateHandlers } from 'modules/UnitOverview/Components/UploadAdditionalFilesDialog';
import { EditUnitInformation } from 'modules/UnitOverview/UnitInformation/CreateEdit/EditUnitInformation';
import { PrintUnitOverviewDialog } from 'modules/UnitOverview/UnitInformation/UnitOverviewPdf/PrintUnitOverviewDialog';
import { BrandModelViewSection } from 'modules/UnitOverview/UnitInformation/View/BrandModelViewSection';
import { DimensionsViewSection } from 'modules/UnitOverview/UnitInformation/View/DimensionsViewSection';
import { IdentifiersSection } from 'modules/UnitOverview/UnitInformation/View/IdentifiersSection';
import { InformationViewSection } from 'modules/UnitOverview/UnitInformation/View/InformationViewSection';

const fileStateHandlers: IAdditionalFileUploadStateHandlers = {
	attachmentFolderCreated,
	fileDeleted,
	fileUploaded,
	fileStateSelector: selectUnitCreationFiles,
	reset: resetUnitCreationState,
};

const InformationSectionPage = () => {
	const { t } = useTranslation(['unitOverview', 'unitCreation']);

	const {
		open: isEdit,
		handleClose: handleEditClose,
		handleOpen: handleEditOpen,
	} = useDialogState(false);
	const {
		open: isPdfOpen,
		handleClose: handlePdfClose,
		handleOpen: handlePdfOpen,
	} = useDialogState(false);
	const unitIds = useResolvedUnitIds();

	const queryClient = useQueryClient();
	const { api } = useProductCenterApi();

	const { data: unitData } = useSuspenseQuery({
		...unitStructureQuery(unitIds.id),
	});

	const { data: unitInfo } = useSuspenseQuery({
		...unitInfoQuery(api, unitIds.id),
	});

	const handleUnitPreviewPdf = useCallback(() => {
		handlePdfOpen();
	}, [handlePdfOpen]);

	const handleOnClose = useCallback(
		(isSubmitting: boolean) => {
			if (isSubmitting) {
				queryClient.invalidateQueries({
					queryKey: unitKeys.structure(unitIds.id),
				});
				queryClient.invalidateQueries({
					queryKey: unitKeys.info(unitIds.id),
				});
			}
		},
		[queryClient, unitIds.id]
	);
	return (
		<>
			<SectionTitleButtons>
				<AuthorizeComponent
					areaName={AreaName.MaintainCoverage}
					permission={OperationType.Read}>
					{
						<SectionButton
							text={t('unitOverview:action-unit-preview')}
							icon={<PdfIcon />}
							onClick={handleUnitPreviewPdf}
						/>
					}
				</AuthorizeComponent>
				<AuthorizeComponent
					areaName={AreaName.Unit}
					permission={OperationType.Write}>
					<SectionButton
						text={t('unitOverview:edit-unit-information-title')}
						icon={<Edit />}
						onClick={handleEditOpen}
					/>
				</AuthorizeComponent>
			</SectionTitleButtons>
			{isPdfOpen && (
				<PrintUnitOverviewDialog
					isOpen={isPdfOpen}
					onClose={handlePdfClose}
					id={unitData?.id ?? 0}
				/>
			)}
			{!isEdit ? (
				<Stack
					direction="column"
					sx={{
						gap: 2,
						my: 2,
					}}>
					<InformationViewSection unitData={unitData} />
					<IdentifiersSection unitData={unitData} />
					<BrandModelViewSection unitData={unitData} />
					{isUnitMarine(unitData?.mainSegment) && (
						<DimensionsViewSection
							measurements={unitInfo?.measurement}
						/>
					)}
					<Box sx={{ alignSelf: 'flex-end' }}>
						<ChangedByInfoDisplay
							changedByInfo={unitInfo?.informationChangedBy}
						/>
					</Box>
					<AdditionalFilesSection
						fileSets={
							unitInfo?.allFileSets != null
								? unitInfo.allFileSets
								: []
						}
						additionalAction={
							<AdditionalFileUploadButton
								handler={fileStateHandlers}
								onClose={handleOnClose}
							/>
						}
						onDelete={handleOnClose}
					/>
				</Stack>
			) : (
				<AuthorizeComponent
					areaName={AreaName.Unit}
					permission={OperationType.Write}>
					<EditUnitInformation
						unitData={unitData}
						unitMeasurement={unitInfo?.measurement}
						onBack={handleEditClose}
					/>
				</AuthorizeComponent>
			)}
		</>
	);
};

export default withAITracking(
	reactPlugin,
	InformationSectionPage,
	'UnitOverviewInformationSectionPage',
	'application-insight-container'
);
